import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import _ from 'lodash';

Vue.use(Vuex);
axios.defaults.baseURL = process.env.VUE_APP_API_URL;

const user = JSON.parse(localStorage.getItem('dmCMS.user'));

if (user) {
  axios.defaults.baseURL = `${process.env.VUE_APP_API_URL}?userId=${user.userId}&projectName=${user.projectName}&authToken=${user.authToken}`;
}

const makeRequest = (params, data) => axios({
  method: data ? 'post' : 'get',
  params,
  data,
}).then(({ data }) => {
  if (data.errors) {
    let title = 'The following errors occurred';
    let msg = _.map(
      data.errors,
      (e) => `&bull; ${e.message}`,
    ).join('<br>');

    if (msg.includes('Authentication failed.') || msg.includes('Session has expired.')) {
      title = 'Please sign in again';
      msg = 'Your session has expired.';
      store.commit('signOut');
      store.commit('authError', store.$app.$route);
      store.$app.$router.push({ name: 'SignIn' });
    }

    store.$app.$emit('show-warning', {
      title,
      msg,
    });

    return Promise.reject(data);
  }

  return data;
});

const store = new Vuex.Store({
  state: {
    loadingMessage: '',
    showGlobalOverlay: false,
    user,
    content: [],
    contentItem: {},
    contentItemRevisions: [],
    blogs: [],
    blogItem: {},
    blogItemRevisions: [],
    files: [],
  },
  getters: {
    isLoggedIn: (state) => !!state.user && !state.loadingMessage.includes('Authorizing'),
    isLoadingActive: (state) => !!state.loadingMessage,
    isGlobalOverlayActive: (state) => !!state.showGlobalOverlay,
  },
  mutations: {
    /* Loading / Busy */
    showLoader(state, data) {
      state.loadingMessage = data.loadingMessage;
      state.showGlobalOverlay = data.showGlobalOverlay;
    },
    hideLoader(state) {
      state.loadingMessage = '';
      state.showGlobalOverlay = false;
    },
    /* / Loading / Busy */

    /* Init App Data */
    initialize(state, data) {
      [
        state.content,
        state.contentItem,
        state.blogs,
        state.blogItem,
        state.files,
      ] = data;
    },
    /* / Init App */

    /* Auth */
    authSuccess(state, user) {
      state.user = user;
    },
    authError(state, route) {
      const referrer = {
        name: route.name,
        params: _.clone(route.params),
      };

      state.referrer = referrer;
    },
    clearAuthError(state) {
      state.referrer = null;
    },
    signOut(state) {
      state.loadingMessage = '';
      state.showGlobalOverlay = false;
      state.user = {};
      state.content = [];
      state.contentItem = {};
      state.contentItemRevisions = [];
      state.blogs = [];
      state.blogItem = {};
      state.blogItemRevisions = [];
      state.files = [];
      axios.defaults.baseURL = process.env.VUE_APP_API_URL;
      localStorage.clear();
    },
    /* / Auth */

    /* Content */
    setContent(state, content) {
      state.content = content;
    },
    setContentItem(state, contentItem) {
      state.contentItem = contentItem;
    },
    setContentItemById(state, contentId) {
      const contentItem = _.find(state.content, { contentId }) || {};
      state.contentItem = _.clone(contentItem);
    },
    setContentItemRevisions(state, revisions) {
      state.contentItemRevisions = revisions;
    },
    /* / Content */

    /* Blog */
    setBlogs(state, blogs) {
      state.blogs = blogs;
    },
    setBlogItem(state, blogItem) {
      state.blogItem = blogItem;
    },
    setBlogItemById(state, postId) {
      const blogItem = _.find(state.blogs, { postId }) || {};
      state.blogItem = _.clone(blogItem);
    },
    setBlogItemRevisions(state, revisions) {
      state.blogItemRevisions = revisions;
    },
    /* / Blog */
  },
  actions: {
    /* Init App */
    initialize({ commit, state }) {
      let initPromise;

      if (state.user) {
        commit('showLoader', {
          loadingMessage: 'Loading data...',
          showGlobalOverlay: true,
        });

        initPromise = makeRequest({
          action: 'itemsGet',
        }).then((content) => {
          const { contentId, blogId } = store.$app.$route.params;
          let contentItem = {};

          if (contentId) {
            contentItem = _.clone(_.find(content, { contentId }));
          }

          const blogsPromise = makeRequest({
            action: 'blogsGet',
          });

          const filesPromise = makeRequest({
            action: 'userFilesGetAll',
          });

          return Promise.all([
            blogsPromise,
            filesPromise,
          ]).then((data) => {
            const [blogs, files] = data;
            let blogItem = {};

            if (blogId) {
              blogItem = _.clone(_.find(blogs, { postId: blogId }));
            }

            commit('initialize', [content, contentItem, blogs, blogItem, files]);
          });
        }).finally(() => {
          commit('hideLoader');
        });
      } else {
        initPromise = Promise.reject(new Error('User is not signed in.'));
      }

      return initPromise;
    },
    /* / Init App */

    /* Auth */
    signIn({ commit }, credentials) {
      const params = _.clone(credentials);

      params.action = 'signIn';

      commit('showLoader', {
        loadingMessage: 'Authorizing...',
      });

      return makeRequest(params)
        .then(({ data }) => {
          const user = {
            projectName: credentials.projectName,
            userId: credentials.userId,
            authToken: data.message,
          };

          axios.defaults.baseURL = `${process.env.VUE_APP_API_URL}?userId=${user.userId}&projectName=${user.projectName}&authToken=${user.authToken}`;

          localStorage.setItem('dmCMS.user', JSON.stringify(user));

          commit('authSuccess', user);

          return user;
        })
        .catch(() => {
          localStorage.removeItem('dmCMS.user');
          return Promise.reject();
        })
        .finally(() => {
          commit('hideLoader');
        });
    },
    signOut({ commit }) {
      const promise = makeRequest({ action: 'signOut' });
      commit('signOut');
      return promise;
    },
    /* / Auth */

    /* Content */
    getContentItem({ commit }, contentId) {
      commit('showLoader', {
        loadingMessage: 'Loading data...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action: 'itemGet',
        contentId,
      }).then((content) => {
        commit('setContentItem', content.data);
      }).finally(() => {
        commit('hideLoader');
      });
    },
    getContentItemRevisions({ commit }, contentId) {
      commit('showLoader', {
        loadingMessage: 'Loading data...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action: 'itemGetBackups',
        contentId,
      }).then((revisions) => {
        commit('setContentItemRevisions', revisions);
      }).finally(() => {
        commit('hideLoader');
      });
    },
    updateContentItem({ commit, state }, action) {
      commit('showLoader', {
        loadingMessage: 'Saving data...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action,
      }, {
        contentId: state.contentItem.contentId,
        content: state.contentItem.content,
      }).then(() => makeRequest({
        action: 'itemsGet',
      })).then((content) => {
        commit('setContent', content);
        commit('setContentItem', {});
        commit('setContentItemRevisions', []);
      }).finally(() => {
        commit('hideLoader');
      });
    },
    removeContentItem({ commit, state }, action) {
      commit('showLoader', {
        loadingMessage: 'Saving data...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action,
      }, {
        contentId: state.contentItem.contentId,
        content: '',
      }).then(() => makeRequest({
        action: 'itemsGet',
      })).then((content) => {
        commit('setContent', content);
        commit('setContentItem', {});
        commit('setContentItemRevisions', []);
      }).finally(() => {
        commit('hideLoader');
      });
    },
    updateActiveContentItemVersion({ commit }, content) {
      commit('showLoader', {
        loadingMessage: 'Saving data...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action: 'itemSetActiveVersion',
        contentId: content.contentId,
        contentVersionNumber: content.version,
      }).then(() => makeRequest({
        action: 'itemsGet',
      })).then((content) => {
        commit('setContent', content);
        commit('setContentItem', {});
        commit('setContentItemRevisions', []);
      }).finally(() => {
        commit('hideLoader');
      });
    },
    /* / Content */

    /* Blog */
    getBlogItemRevisions({ commit }, postId) {
      commit('showLoader', {
        loadingMessage: 'Loading data...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action: 'blogGetBackups',
        postId,
      }).then((revisions) => {
        commit('setBlogItemRevisions', revisions);
      }).finally(() => {
        commit('hideLoader');
      });
    },
    createBlogItem({ commit, state }) {
      commit('showLoader', {
        loadingMessage: 'Saving data...',
        showGlobalOverlay: true,
      });

      const data = _.cloneDeep(state.blogItem);

      return makeRequest({
        action: 'blogCreate',
      }, data).then(() => makeRequest({
        action: 'blogsGet',
      })).then((blogs) => {
        commit('setBlogs', blogs);
        commit('setBlogItem', {});
      }).finally(() => {
        commit('hideLoader');
      });
    },
    updateBlogItem({ commit, state }) {
      commit('showLoader', {
        loadingMessage: 'Saving data...',
        showGlobalOverlay: true,
      });

      const data = _.cloneDeep(state.blogItem);

      return makeRequest({
        action: 'blogUpdate',
      }, data).then(() => makeRequest({
        action: 'blogsGet',
      })).then((blogs) => {
        commit('setBlogs', blogs);
        commit('setBlogItem', {});
        commit('setBlogItemRevisions', []);
      }).finally(() => {
        commit('hideLoader');
      });
    },
    updateActiveBlogItemVersion({ commit }, blog) {
      commit('showLoader', {
        loadingMessage: 'Saving data...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action: 'blogSetActiveVersion',
        postId: blog.postId,
        contentVersionNumber: blog.version,
      }).then(() => makeRequest({
        action: 'blogsGet',
      })).then((blogs) => {
        commit('setBlogs', blogs);
        commit('setBlogItem', {});
        commit('setBlogItemRevisions', []);
      }).finally(() => {
        commit('hideLoader');
      });
    },
    /* / Blog */

    /* Files */
    uploadFile({ state, commit }, payload = {}) {
      commit('showLoader', {
        loadingMessage: 'Uploading file...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action: 'userFileSave',
        userFileName: payload.file.name,
      }, payload.file).then(() => makeRequest({
        action: 'userFilesGetAll',
      })).then((files) => {
        state.files = files;
      }).finally(() => {
        commit('hideLoader');
      });
    },
    deleteFile({ state, commit }, payload = {}) {
      commit('showLoader', {
        loadingMessage: 'Deleting file...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action: 'userFileDelete',
        userFileName: payload.fileName,
      }).then(() => makeRequest({
        action: 'userFilesGetAll',
      })).then((files) => {
        state.files = files;
      }).finally(() => {
        commit('hideLoader');
      });
    },
    /* / Files */

    /* Preview / Publish */
    preview({ commit }, generatePlaceholders) {
      commit('showLoader', {
        loadingMessage: 'Previewing site...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action: 'previewSite',
        placeHolderImages: generatePlaceholders,
      }).finally(() => {
        commit('hideLoader');
      });
    },
    publish({ commit }) {
      commit('showLoader', {
        loadingMessage: 'Publishing site...',
        showGlobalOverlay: true,
      });

      return makeRequest({
        action: 'publishSite',
      }).finally(() => {
        commit('hideLoader');
      });
    },
    /* / Preview / Publish */
  },
});

export default store;
